import React, { useEffect, useState } from "react";
import Dropdown from "../../input/Dropdown";
import {
  getBulkDropdownData,
  getFieldValuesByTableAndId,
} from "../../../api/keyvalue";
import Input from "../../input/Input";
import Button from "../../input/Button";
import Grid from "../../Grid";
import { ResizableScrollingTextRenderer } from "../../renderer/StyleRenderer";
import ButtonRenderer from "../../renderer/ButtonRenderer";
import { getRequestOtherRequests } from "../../../api/jobModificationApi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { chargesByCustomer } from "../../../api/customerChargesApi";
import LoadingSpinner from "../../LoadingSpinner";

const JobOtherChange = ({
  editMode,
  requestId,
  isOpen,
  otherRequests,
  setOtherRequests,
  customer,
}) => {
  const [requestOther, setRequestOther] = useState({
    rotId: null,
    rotChargeType: null,
    rotValue: "",
    rotRemarks: "",
    rotStatus: 2,
  });

  const [isAddLoading, setIsAddLoading] = useState(false);
  const [isEditingOther, setIsEditingOther] = useState(false);
  const [loadingOtherRequests, setLoadingOtherRequests] = useState(false);
  const [errors, setErrors] = useState({});
  const [chargeTypeOptions, setChargeTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [chargeTypeLoading, setChargeTypeLoading] = useState(false);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        setChargeTypeLoading(true);
        const { status: statusTypeData } = await getBulkDropdownData([
          { key: "status" },
        ]);

        if (customer) {
          const data = await chargesByCustomer(customer);
          setChargeTypeOptions(data);
        } else {
          setChargeTypeOptions([]);
        }

        setStatusOptions(statusTypeData || []);
      } catch (error) {
        console.error("Error loading dropdown options:", error);
      } finally {
        setChargeTypeLoading(false);
      }
    };
    if (isOpen) {
      loadDropdowns();
    }
  }, [customer, isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      requestId &&
      chargeTypeOptions.length > 0 &&
      statusOptions.length > 0
    ) {
      fetchExistingOtherRequests();
    }
  }, [isOpen, requestId, chargeTypeOptions.length, statusOptions.length]);

  const fetchExistingOtherRequests = async () => {
    try {
      const existingOthers = await getRequestOtherRequests(requestId);

      if (!existingOthers || existingOthers.length === 0) {
        setOtherRequests([]);
        setLoadingOtherRequests(false);
        return;
      }

      const chargeTypeIds = existingOthers
        .map((other) => other.rotChargeType)
        .filter((id) => id != null);

      const chargeTypeNamesMap = {};

      for (const id of chargeTypeIds) {
        try {
          const result = await getFieldValuesByTableAndId({
            tableName: "masterdata.tms_vm_stakeholder_charge_types",
            primaryKey: "chp_id",
            fieldNames: "cht_charge",
            ids: id,
          });

          if (result && result.length > 0 && result[0].field1) {
            chargeTypeNamesMap[id] = result[0].field1;
          } else {
            const optionName = chargeTypeOptions.find(
              (opt) => opt.value === id,
            )?.text;
            if (optionName) {
              chargeTypeNamesMap[id] = optionName;
            }
          }
        } catch (error) {
          console.error(`Error fetching charge type name for ID ${id}:`, error);

          const optionName = chargeTypeOptions.find(
            (opt) => opt.value === id,
          )?.text;
          if (optionName) {
            chargeTypeNamesMap[id] = optionName;
          }
        }
      }

      const mappedOthers = existingOthers.map((other) => {
        const chargeTypeName =
          chargeTypeNamesMap[other.rotChargeType] ||
          `ID: ${other.rotChargeType}`;
        const statusText =
          statusOptions.find((opt) => opt.value === other.rotStatus)?.text ||
          "";

        return {
          ...other,
          action: null,
          rotChargeTypeValue: chargeTypeName,
          rotStatusValue: statusText,
        };
      });

      setOtherRequests(mappedOthers);
    } catch (error) {
      console.error("Error loading other requests:", error);

      toast.error("Failed to load existing other requests", {
        autoClose: 2000,
        containerId: "Modal",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!requestOther.rotChargeType) {
      newErrors.rotChargeType = "Charge type is required";
    }

    if (!requestOther.rotValue || parseFloat(requestOther.rotValue) <= 0) {
      newErrors.rotValue = "Value must be greater than 0";
    }

    if (!requestOther.rotStatus) {
      newErrors.rotStatus = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const handleAddOrUpdate = async () => {
  if (!validateForm()) {
    return;
  }

  const isDuplicate = otherRequests.some((other) => {
    if (other.action === "DELETE") return false;

    if (isEditingOther) {
      const isCurrentRecord = requestOther.rotId !== null
        ? other.rotId === requestOther.rotId
        : other === otherRequests.find(
            (o) =>
              o.rotChargeType === requestOther.originalChargeType &&
              o.rotValue === parseFloat(requestOther.originalValue) &&
              o.action === "ADD"
          );

      if (isCurrentRecord) return false;
    }

    return other.rotChargeType === requestOther.rotChargeType;
  });

  if (isDuplicate) {
    toast.error("This charge type has already been added", {
      autoClose: 2000,
      containerId: "Modal",
    });
    setIsAddLoading(false);
    return;
  }

  setIsAddLoading(true);

  let chargeTypeName = "";
  try {
    const chargeTypeValue = await getFieldValuesByTableAndId({
      tableName: "masterdata.tms_vm_stakeholder_charge_types",
      primaryKey: "chp_id",
      fieldNames: "cht_charge",
      ids: requestOther.rotChargeType,
    });

    if (
      chargeTypeValue &&
      chargeTypeValue.length > 0 &&
      chargeTypeValue[0].field1
    ) {
      chargeTypeName = chargeTypeValue[0].field1;
    } else {
      const optionName = chargeTypeOptions.find(
        (opt) => opt.value === requestOther.rotChargeType,
      )?.text;
      chargeTypeName = optionName || `ID: ${requestOther.rotChargeType}`;
    }
  } catch (error) {
    console.error("Error fetching charge type name:", error);
    const optionName = chargeTypeOptions.find(
      (opt) => opt.value === requestOther.rotChargeType,
    )?.text;
    chargeTypeName = optionName || `ID: ${requestOther.rotChargeType}`;
  }

  const statusText =
    statusOptions.find((opt) => opt.value === requestOther.rotStatus)?.text ||
    "";

  if (isEditingOther) {
    const isExistingRecord = requestOther.rotId !== null;

    setOtherRequests((prev) =>
      prev.map((other) => {
        const isThisRecord =
          requestOther.rotId !== null
            ? other.rotId === requestOther.rotId
            : other ===
              prev.find(
                (o) =>
                  o.rotChargeType === requestOther.originalChargeType &&
                  o.rotValue === parseFloat(requestOther.originalValue) &&
                  o.action === "ADD",
              );

        if (isThisRecord) {
          return {
            ...other,
            rotChargeType: requestOther.rotChargeType,
            rotChargeTypeValue: chargeTypeName,
            rotValue: parseFloat(requestOther.rotValue),
            rotRemarks: requestOther.rotRemarks,
            rotStatus: requestOther.rotStatus,
            rotStatusValue: statusText,

            action: isExistingRecord ? "EDIT" : "ADD",
          };
        }
        return other;
      }),
    );

    setIsEditingOther(false);
    toast.success("Record updated", {
      autoClose: 1000,
      containerId: "Modal",
    });
  } else {
    const newOther = {
      rotId: null,
      rotChargeType: requestOther.rotChargeType,
      rotChargeTypeValue: chargeTypeName,
      rotValue: parseFloat(requestOther.rotValue),
      rotRemarks: requestOther.rotRemarks,
      rotStatus: requestOther.rotStatus,
      rotStatusValue: statusText,
      action: "ADD",
    };

    setOtherRequests((prev) => [...prev, newOther]);

    toast.success("Record added", {
      autoClose: 1000,
      containerId: "Modal",
    });
  }

  setIsAddLoading(false);
  resetForm();
};

  const handleEdit = (other) => {
    setRequestOther({
      rotId: other.rotId,
      rotChargeType: other.rotChargeType,
      rotValue: other.rotValue.toString(),
      rotRemarks: other.rotRemarks || "",
      rotStatus: other.rotStatus,
      originalChargeType: other.rotChargeType,
      originalValue: other.rotValue,
    });

    setIsEditingOther(true);
  };

  const handleDelete = (other) => {
    if (other.rotId) {
      setOtherRequests((prev) =>
        prev.map((o) =>
          o.rotId === other.rotId ? { ...o, action: "DELETE" } : o,
        ),
      );

      toast.success("Record marked for deletion", {
        autoClose: 1000,
        containerId: "Modal",
      });
    } else {
      setOtherRequests((prev) => prev.filter((o) => o !== other));

      toast.success("Record removed", {
        autoClose: 1000,
        containerId: "Modal",
      });
    }
  };

  const resetForm = () => {
    setRequestOther({
      rotId: null,
      rotChargeType: null,
      rotValue: "",
      rotRemarks: "",
      rotStatus: 2,
    });
    setIsEditingOther(false);
    setErrors({});
  };

  useEffect(() => {
    if (!isOpen) {
      setRequestOther({
        rotId: null,
        rotChargeType: null,
        rotValue: "",
        rotRemarks: "",
        rotStatus: 2,
      });
      setErrors({});
      setIsEditingOther(false);
    }
  }, [isOpen]);

  const resetError = (field) => {
    setErrors((prevState) => {
      const { [field]: _, ...rest } = prevState;
      return rest;
    });
  };

  const displayList = otherRequests.filter(
    (other) => other.action !== "DELETE",
  );

  return (
    <div>
      {chargeTypeLoading ? (
        <LoadingSpinner />
      ) : (
        <div style={{ width: "100%" }}>
          <div className="ui segment">
            <h5>
              {isEditingOther ? "Edit Other Request" : "Add Other Request"}
            </h5>
            <div className="two fields">
              <Dropdown
                label="Charge Type"
                required={true}
                error={errors.rotChargeType}
                field="rot_charge_type"
                resetError={() => resetError("rotChargeType")}
                name="charge_type"
                style={{ height: "20px" }}
                options={chargeTypeOptions}
                value={requestOther.rotChargeType}
                onChange={(e, data) => {
                  setRequestOther((prevState) => ({
                    ...prevState,
                    rotChargeType: data.value,
                  }));
                  resetError("rotChargeType");
                }}
              />
              <Input
                label="Value"
                required={true}
                error={errors.rotValue}
                field="rot_value"
                resetError={() => resetError("rotValue")}
                type="number"
                step="0.01"
                min={0}
                name="value"
                placeholder="0.00"
                value={requestOther.rotValue}
                onChange={(e) =>
                  setRequestOther((prevState) => ({
                    ...prevState,
                    rotValue: e.target.value,
                  }))
                }
              />
            </div>
            <div className="two fields">
              <Input
                label="Remark"
                error={errors.rotRemarks}
                field="rot_remarks"
                resetError={() => resetError("rotRemarks")}
                name="remarks"
                placeholder="Enter remark"
                value={requestOther.rotRemarks}
                onChange={(e) =>
                  setRequestOther((prevState) => ({
                    ...prevState,
                    rotRemarks: e.target.value,
                  }))
                }
              />

              <Dropdown
                required={true}
                label="Status"
                error={errors.rotStatus}
                field="rot_status"
                resetError={() => resetError("rotStatus")}
                name="status"
                style={{ height: "20px" }}
                options={statusOptions}
                value={requestOther.rotStatus}
                onChange={(e, data) => {
                  setRequestOther((prevState) => ({
                    ...prevState,
                    rotStatus: data.value,
                  }));
                  resetError("rotStatus");
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button label="Reset" onClick={resetForm} className="ui button" />
              <Button
                label={isEditingOther ? "Update" : "Add"}
                onClick={handleAddOrUpdate}
                disabled={isAddLoading}
                className="ui primary button"
              />
            </div>
          </div>

          <div style={{ width: "100%", marginTop: "20px" }}>
            <Grid
              heading="Other Information List"
              config={[
                {
                  headerName: "Charge Type",
                  field: "rotChargeTypeValue",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Value",
                  field: "rotValue",
                  valueFormatter: (params) => {
                    const value = parseFloat(params.value);
                    return isNaN(value) ? "0.00" : value.toFixed(2);
                  },
                },
                {
                  headerName: "Remark",
                  field: "rotRemarks",
                },
                {
                  headerName: "Status",
                  field: "rotStatusValue",
                },
                {
                  field: "Edit",
                  filter: false,
                  cellRenderer: ButtonRenderer,
                  cellRendererParams: (params) => ({
                    clickHandler: () => handleEdit(params.data),
                    label: "Edit",
                  }),
                },
                {
                  field: "Delete",
                  filter: false,
                  cellRenderer: ButtonRenderer,
                  cellRendererParams: (params) => ({
                    clickHandler: () => handleDelete(params.data),
                    label: "Delete",
                    className: "ui red button",
                  }),
                },
              ]}
              content={displayList}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobOtherChange;
